import React from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, ZoomControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import MapTileToggle from "@/components/maps/MapTileToggle";
import {
  MapPin, Car, Bus, Plane, Phone, Mail, MessageCircle, Clock, Compass, ArrowUpRight,
  Bell, ConciergeBell, UtensilsCrossed,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getHotelLocation } from "@/lib/hotelLocations";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// Inline SVG pin (no external icon assets) — matches the brand gold
const xalucaPin = L.divIcon({
  className: "xaluca-map-pin",
  html: `<div style="position:relative;width:32px;height:42px;">
    <svg viewBox="0 0 32 42" width="32" height="42" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 0C7.16 0 0 7.16 0 16c0 12 16 26 16 26s16-14 16-26C32 7.16 24.84 0 16 0z"
            fill="#C16542" stroke="#FDFBF7" stroke-width="1.5"/>
      <circle cx="16" cy="16" r="6" fill="#FDFBF7"/>
    </svg>
  </div>`,
  iconSize: [32, 42],
  iconAnchor: [16, 42],
  popupAnchor: [0, -38],
});

const COPY = {
  overline: { en: "Location · Contact", fr: "Adresse · Contact", es: "Ubicación · Contacto" },
  title: { en: "How to find us,", fr: "Pour nous rejoindre,", es: "Cómo encontrarnos," },
  title_accent: { en: "how to reach us.", fr: "pour nous joindre.", es: "cómo contactarnos." },
  address: { en: "Address", fr: "Adresse", es: "Dirección" },
  by_car: { en: "By car", fr: "En voiture", es: "En coche" },
  public_transport: { en: "Public transport", fr: "Transports publics", es: "Transporte público" },
  by_air: { en: "By air", fr: "En avion", es: "En avión" },
  contact: { en: "Contact", fr: "Contact", es: "Contacto" },
  hours: { en: "Hours", fr: "Horaires", es: "Horarios" },
  open_in_maps: { en: "Open in Maps", fr: "Ouvrir dans Maps", es: "Abrir en Maps" },
  cta_enquiry: { en: "Send an enquiry", fr: "Envoyer une demande", es: "Enviar una consulta" },
  cta_visit: { en: "Plan a stay", fr: "Préparer un séjour", es: "Planificar una estancia" },
};

const HotelLocationContact = ({ hotel }) => {
  const { lang } = useLanguage();
  const loc = getHotelLocation(hotel.slug, hotel);
  const [lat, lng] = loc.coords;
  const mapsUrl = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=14/${lat}/${lng}`;
  const gmapsUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

  return (
    <section
      id="location-contact"
      className="py-24 md:py-32 bg-[#FDFBF7] border-t border-[#2C2621]/10"
      data-testid="section-location-contact"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-8 mb-14 md:mb-16">
          <div className="md:col-span-7">
            <p className="overline flex items-center gap-2">
              <Compass className="w-3.5 h-3.5 text-[#A07042]" strokeWidth={1.6} />
              {pick(COPY.overline, lang)}
            </p>
            <h2 className="font-serif-x mt-5 text-4xl md:text-5xl lg:text-[56px] leading-[1.02] tracking-tight text-[#2C2621]">
              {pick(COPY.title, lang)}{" "}
              <em className="not-italic text-[#C16542]">{pick(COPY.title_accent, lang)}</em>
            </h2>
          </div>
          <div className="md:col-span-5 flex md:items-end">
            <div className="flex items-start gap-3 text-[#5C5248]">
              <MapPin className="w-5 h-5 text-[#A07042] mt-1 shrink-0" strokeWidth={1.5} />
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248]/70 mb-1">{pick(COPY.address, lang)}</p>
                <p className="font-serif-x text-lg md:text-xl text-[#2C2621] leading-snug">{pick(loc.address, lang)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Map + practical info — 12-col layout */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Map */}
          <div className="lg:col-span-7 relative" data-testid="location-map-wrapper">
            <div className="relative h-[420px] md:h-[520px] overflow-hidden border border-[#2C2621]/10 shadow-[0_30px_80px_-50px_rgba(26,21,19,0.45)]">
              <MapContainer
                center={[lat, lng]}
                zoom={13}
                scrollWheelZoom={false}
                zoomControl={false}
                attributionControl={true}
                className="absolute inset-0 w-full h-full"
                data-testid="location-map"
              >
                <MapTileToggle
                  mapUrl="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                  mapAttribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                  mapSubdomains={['a','b','c','d']}
                  mapMaxZoom={20}
                />
                <ZoomControl position="bottomright" />
                <Marker position={[lat, lng]} icon={xalucaPin}>
                  <Popup>
                    <strong>{hotel.name}</strong>
                    <br />
                    {pick(loc.address, lang)}
                  </Popup>
                </Marker>
              </MapContainer>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <a
                href={gmapsUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-2 bg-[#2C2621] hover:bg-[#1A1513] text-[#FDFBF7] px-4 py-2.5 text-[10px] tracking-[0.3em] uppercase transition-colors"
                data-testid="location-open-maps"
              >
                <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
                {pick(COPY.open_in_maps, lang)}
                <ArrowUpRight className="w-3 h-3" strokeWidth={1.5} />
              </a>
              <a
                href={directionsUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-2 border border-[#2C2621]/25 hover:border-[#2C2621] text-[#2C2621] px-4 py-2.5 text-[10px] tracking-[0.3em] uppercase transition-colors"
                data-testid="location-get-directions"
              >
                <Compass className="w-3.5 h-3.5" strokeWidth={1.5} />
                {pick({ en: "Get directions", fr: "Itinéraire", es: "Cómo llegar" }, lang)}
              </a>
              <a
                href={mapsUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="text-[10px] tracking-[0.3em] uppercase text-[#5C5248]/70 hover:text-[#C16542] ml-auto transition-colors"
              >
                OpenStreetMap ↗
              </a>
            </div>
          </div>

          {/* Practical info columns */}
          <div className="lg:col-span-5 space-y-8">
            {/* Getting there */}
            <div data-testid="location-getting-there">
              <p className="overline mb-4">{pick({ en: "Getting there", fr: "Pour s'y rendre", es: "Cómo llegar" }, lang)}</p>
              <ul className="space-y-5">
                <li className="flex gap-4">
                  <span className="inline-flex items-center justify-center w-10 h-10 border border-[#D4A373]/45 text-[#A07042] shrink-0">
                    <Car className="w-4 h-4" strokeWidth={1.5} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-serif-x text-base md:text-lg text-[#2C2621] leading-snug">{pick(COPY.by_car, lang)}</h4>
                    <p className="mt-1 text-sm text-[#5C5248] leading-relaxed">{pick(loc.getting_there.car, lang)}</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="inline-flex items-center justify-center w-10 h-10 border border-[#D4A373]/45 text-[#A07042] shrink-0">
                    <Bus className="w-4 h-4" strokeWidth={1.5} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-serif-x text-base md:text-lg text-[#2C2621] leading-snug">{pick(COPY.public_transport, lang)}</h4>
                    <p className="mt-1 text-sm text-[#5C5248] leading-relaxed">{pick(loc.getting_there.transport, lang)}</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="inline-flex items-center justify-center w-10 h-10 border border-[#D4A373]/45 text-[#A07042] shrink-0">
                    <Plane className="w-4 h-4" strokeWidth={1.5} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-serif-x text-base md:text-lg text-[#2C2621] leading-snug">{pick(COPY.by_air, lang)}</h4>
                    <p className="mt-1 text-sm text-[#5C5248] leading-relaxed">{pick(loc.getting_there.airport, lang)}</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Contact + Hours strip */}
            <div className="grid grid-cols-1 gap-px bg-[#2C2621]/10 border border-[#2C2621]/10" data-testid="location-contact-hours">
              <div className="bg-[#FDFBF7] p-6">
                <p className="overline mb-3 inline-flex items-center gap-2">
                  <Phone className="w-3 h-3 text-[#A07042]" strokeWidth={1.6} />
                  {pick(COPY.contact, lang)}
                </p>
                <ul className="space-y-2.5 text-sm">
                  <li>
                    <a href={`tel:${loc.phone.replace(/[^\d+]/g, "")}`} className="inline-flex items-center gap-2 text-[#2C2621] hover:text-[#C16542] transition-colors" data-testid="location-phone">
                      <Phone className="w-3.5 h-3.5 text-[#A07042]" strokeWidth={1.5} />
                      {loc.phone}
                    </a>
                  </li>
                  <li>
                    <a href={`mailto:${loc.email}`} className="inline-flex items-center gap-2 text-[#2C2621] hover:text-[#C16542] transition-colors" data-testid="location-email">
                      <Mail className="w-3.5 h-3.5 text-[#A07042]" strokeWidth={1.5} />
                      {loc.email}
                    </a>
                  </li>
                  {loc.whatsapp && (
                    <li>
                      <a
                        href={`https://wa.me/${loc.whatsapp.replace(/[^\d]/g, "")}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-2 text-[#2C2621] hover:text-[#C16542] transition-colors"
                        data-testid="location-whatsapp"
                      >
                        <MessageCircle className="w-3.5 h-3.5 text-[#A07042]" strokeWidth={1.5} />
                        {loc.whatsapp}
                      </a>
                    </li>
                  )}
                </ul>
              </div>
              <div className="bg-[#FDFBF7] p-6">
                <p className="overline mb-3 inline-flex items-center gap-2">
                  <Clock className="w-3 h-3 text-[#A07042]" strokeWidth={1.6} />
                  {pick(COPY.hours, lang)}
                </p>
                <ul className="space-y-2.5 text-sm text-[#5C5248]">
                  <li className="flex items-start gap-2.5">
                    <Bell className="w-3.5 h-3.5 mt-0.5 text-[#A07042] shrink-0" strokeWidth={1.5} />
                    <span data-testid="location-hours-reception">{pick(loc.hours.reception, lang)}</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <ConciergeBell className="w-3.5 h-3.5 mt-0.5 text-[#A07042] shrink-0" strokeWidth={1.5} />
                    <span data-testid="location-hours-concierge">{pick(loc.hours.concierge, lang)}</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <UtensilsCrossed className="w-3.5 h-3.5 mt-0.5 text-[#A07042] shrink-0" strokeWidth={1.5} />
                    <span data-testid="location-hours-restaurant">{pick(loc.hours.restaurant, lang)}</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Link
                to={`/booking?hotel=${encodeURIComponent(hotel.slug)}`}
                className="group inline-flex items-center justify-center gap-3 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-6 py-3.5 text-[10px] tracking-[0.3em] uppercase transition-colors"
                data-testid="location-cta-booking"
              >
                {pick(COPY.cta_visit, lang)}
                <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.5} />
              </Link>
              <Link
                to={`/contact?topic=stay-${encodeURIComponent(hotel.slug)}`}
                className="group inline-flex items-center justify-center gap-3 border border-[#2C2621]/30 hover:border-[#2C2621] text-[#2C2621] px-6 py-3.5 text-[10px] tracking-[0.3em] uppercase transition-colors"
                data-testid="location-cta-enquiry"
              >
                {pick(COPY.cta_enquiry, lang)}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HotelLocationContact;
